/**
 * CardCollaboratorsSection — seção "Colaboradores" no card (UI-1 / TD-V2-PERM-01).
 *
 * Usado em DealDetailDialog + OsDetailDialog. Combobox multi-select dos users
 * do dept (ou todos, simplificado nesta rodada) + lista de avatares + remover.
 *
 * Feature flag CARD_COLLABORATORS_ENABLED gateaa POST/DELETE no backend —
 * UI mostra mensagem amigável se 503.
 */

import { useMemo, useState } from 'react';

import { Alert, Avatar, Button, Select, useConfirmDialog } from '@/components/ui';
import { useUsers } from '@/lib/admin-store';
import {
  useCardCollaborators,
  useCardCollaboratorActions,
} from '@/lib/card-collaborators-store';
import { ApiError } from '@/lib/api';

interface CardCollaboratorsSectionProps {
  cardId: string;
  /** Filtro opcional: só users do dept do card. Quando null, mostra todos ativos. */
  deptSlug?: string | null;
}

export function CardCollaboratorsSection({ cardId, deptSlug }: CardCollaboratorsSectionProps) {
  const { data: collaborators = [], isLoading, error } = useCardCollaborators(cardId);
  const actions = useCardCollaboratorActions(cardId);
  const allUsers = useUsers();
  const confirm = useConfirmDialog();
  const [selectedUserId, setSelectedUserId] = useState('');
  const [actionError, setActionError] = useState<string | null>(null);

  // Filtra users elegíveis: ativos, do dept (se deptSlug), e que ainda NÃO são collaborators.
  const availableUsers = useMemo(() => {
    const existing = new Set(collaborators.map((c) => c.userId));
    return allUsers.filter((u) => {
      if (!u.ativo) return false;
      if (existing.has(u.id)) return false;
      if (deptSlug && !u.departamentos?.some((dr) => dr.departmentSlug === deptSlug)) {
        return false;
      }
      return true;
    });
  }, [allUsers, collaborators, deptSlug]);

  async function handleAdd() {
    if (!selectedUserId) return;
    setActionError(null);
    try {
      await actions.add.mutateAsync(selectedUserId);
      setSelectedUserId('');
    } catch (err) {
      if (err instanceof ApiError && err.status === 503) {
        setActionError(
          'Feature de colaboradores está desativada. Pergunte ao admin pra ativar em /admin/feature-flags.',
        );
      } else if (err instanceof ApiError && err.status === 403) {
        setActionError('Você não pode adicionar colaboradores (precisa ser admin ou responsável).');
      } else {
        setActionError(err instanceof Error ? err.message : 'Erro ao adicionar colaborador');
      }
    }
  }

  async function handleRemove(userId: string, nome: string) {
    // FASE 2 / M1: ConfirmDialog do design system (substitui confirm() nativo)
    const ok = await confirm({
      title: 'Remover colaborador',
      message: `Remover ${nome} dos colaboradores deste card?`,
      confirmLabel: 'Remover',
      variant: 'destructive',
    });
    if (!ok) return;
    setActionError(null);
    try {
      await actions.remove.mutateAsync(userId);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Erro ao remover');
    }
  }

  // 503 do GET implica feature flag off — mostra disclaimer informativo.
  if (error instanceof ApiError && error.status === 503) {
    return (
      <div data-testid="collaborators-flag-off" className="text-xs text-ink-3 dark:text-ink-dark-3 italic">
        Feature de colaboradores ainda não ativada neste ambiente.
      </div>
    );
  }

  return (
    <div data-testid="card-collaborators-section">
      <p className="eyebrow mb-2">Colaboradores</p>

      {actionError && (
        <Alert variant="danger" rail className="mb-2 text-xs">
          {actionError}
        </Alert>
      )}

      {isLoading ? (
        <p className="text-xs italic text-ink-3 dark:text-ink-dark-3">Carregando…</p>
      ) : collaborators.length === 0 ? (
        <p className="text-xs italic text-ink-3 dark:text-ink-dark-3 mb-2" data-testid="collaborators-empty">
          Nenhum colaborador adicionado ainda.
        </p>
      ) : (
        <ul data-testid="collaborators-list" className="space-y-1 mb-2">
          {collaborators.map((c) => (
            <li
              key={c.userId}
              data-testid={`collaborator-${c.userId}`}
              className="flex items-center justify-between gap-2 text-sm"
            >
              <div className="flex items-center gap-2 min-w-0">
                <Avatar name={c.nome} src={c.fotoUrl ?? undefined} size="xs" />
                <span className="truncate">{c.nome}</span>
              </div>
              <button
                type="button"
                onClick={() => handleRemove(c.userId, c.nome)}
                className="text-ink-3 hover:text-red transition-colors text-sm"
                aria-label={`Remover ${c.nome}`}
                data-testid={`collaborator-remove-${c.userId}`}
                disabled={actions.pending === `del-${c.userId}`}
              >
                ✕
              </button>
            </li>
          ))}
        </ul>
      )}

      {/* Combobox pra adicionar */}
      <div className="flex items-center gap-2">
        <Select
          value={selectedUserId}
          onChange={(e) => setSelectedUserId(e.target.value)}
          data-testid="collaborator-select"
          size="sm"
          className="flex-1"
        >
          <option value="">Adicionar colaborador…</option>
          {availableUsers.map((u) => (
            <option key={u.id} value={u.id}>
              {u.nome}
            </option>
          ))}
        </Select>
        <Button
          type="button"
          size="sm"
          variant="secondary"
          onClick={handleAdd}
          disabled={!selectedUserId || actions.pending?.startsWith('add-')}
          loading={actions.pending === `add-${selectedUserId}`}
          data-testid="collaborator-add-btn"
        >
          + Adicionar
        </Button>
      </div>
    </div>
  );
}
